import AuthService from '../ports/AuthService';
import axios, { AxiosRequestConfig } from 'axios';
import serializeError from 'serialize-error';
import superagent from 'superagent';
import { Location } from '../models/location';
import { Truck, TruckFormData, Template } from '../models/truck';
import { DateString, Dictionary } from '../models/utils';
import LocationService from '../ports/LocationService';
import { LinehaulCompany } from '../models/linehaul-company';
import { CustomsBroker } from '../models/customs-broker';
import { LoadingRestriction } from '../models/loading-restriction';
import { InternalShipmentGroup, ShipmentGroupConfiguration } from '../models/shipment-group';
import TruckService from '../ports/TruckService';
import LoadingRestrictionService from '../ports/LoadingRestrictionService';
import LinehaulCompanyService from '../ports/LinehaulCompanyService';
import CustomsBrokerService from '../ports/CustomsBrokerService';
import ShipmentGroupService, {
  CreateShipmentGroupConfigurationInput,
  UpdateShipmentGroupConfigurationInput,
   DeleteShipmentGroupConfigurationInput,
} from '../ports/ShipmentGroupService';
import Provider from '../ports/Provider';
import { Document } from '../models/document';
import DocumentService from '../ports/DocumentService';
import { ManifestEventLog } from '../models/manifest';
import { ManifestActorName } from '../ports/ManifestService';
import LoggingService, { LogEvent } from '../ports/LoggingService';
import FeatureFlagsService from '../ports/FeatureFlagsService';
import { GetShipmentsQuery, GetShipmentsResult, ShipmentsService } from '../ports/ShipmentsService';
import { CreateWorkflowCommand, GetWorkflowsQuery, Workflow, WorkflowLogQuery, WorkflowLogQueryResults, WorkflowsQueryResults, WorkflowsService } from '../ports/WorkflowService';

export enum HTTPMethod {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  DELETE = 'delete',
}

export interface BackendApiOptions {
  url: string;
  method: HTTPMethod;
  query?: Dictionary<unknown> | URLSearchParams;
  data?: unknown;
  responseType?: string;
}

export default class SOPRestService implements TruckService, LocationService, LoadingRestrictionService,
  LinehaulCompanyService, CustomsBrokerService, ShipmentGroupService, DocumentService, LoggingService,
  FeatureFlagsService, ShipmentsService, WorkflowsService {
  constructor(
    private authService: AuthService,
    private locationProvider: Provider<Location>,
  ) { }

  get baseUrl() {
    return `/api/v1/locations/${this.locationProvider().key}`;
  }

  get devUrl() {
    return `/api/dev/${this.locationProvider().key}`;
  }

  async getLocations() {
    return await this.request<Location[]>({
      method: HTTPMethod.GET,
      url: '/api/v1/locations',
    });
  }

  async getTrucks(selectedDate: DateString) {
    return await this.request<Truck[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/trucks`,
      query: {
        selectedDate,
      },
    });
  }

  async patchTruck(truck: TruckFormData) {
    return await this.request<Truck>({
      method: HTTPMethod.PATCH,
      url: `${this.baseUrl}/trucks/${truck.id!}`,
      data: {
        name: truck.name,
        linehaulCompanyId: truck.linehaulCompanyId,
        crossesBorder: truck.crossesBorder,
        loadingRestrictions: truck.loadingRestrictions,
        customsBrokerId: truck.customsBrokerId,
      },
    });
  }

  async createTruck(truck: TruckFormData) {
    return await this.request<Truck>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/trucks`,
      data: {
        name: truck.name,
        crossesBorder: truck.crossesBorder,
        linehaulCompanyId: truck.linehaulCompanyId,
        loadingRestrictions: truck.loadingRestrictions,
        customsBrokerId: truck.customsBrokerId,
      },
    });
  }

  async deleteTruck(truck: Truck) {
    return await this.request<void>({
      method: HTTPMethod.DELETE,
      url: `${this.baseUrl}/trucks/${truck.id}`,
    });
  }

  async getTruckTemplates() {
    return await this.request<Template[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/trucks/templates`,
    });
  }

  async createTruckTemplate(template: Omit<Template, 'id'>) {
    return await this.request<Template>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/trucks/templates/`,
      data: template,
    });
  }

  async updateTruckTemplate(template: Template) {
    return await this.request<Template>({
      method: HTTPMethod.PATCH,
      url: `${this.baseUrl}/trucks/templates/${template.id}`,
      data: template,
    });
  }

  async deleteTruckTemplate(template: Template) {
    return await this.request<void>({
      method: HTTPMethod.DELETE,
      url: `${this.baseUrl}/trucks/templates/${template.id}`,
    });
  }

  async getShipmentGroupConfigurations() {
    return await this.request<ShipmentGroupConfiguration[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/shipment-group-configurations`,
    });
  }

  async createShipmentGroupConfiguration(input: CreateShipmentGroupConfigurationInput) {
    return await this.request<ShipmentGroupConfiguration>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/shipment-group-configurations`,
      data: {
        ...input,
        locationKey: this.locationProvider().key,
      },
    });
  }

  async updateShipmentGroupConfiguration(input: UpdateShipmentGroupConfigurationInput) {
    return await this.request<ShipmentGroupConfiguration>({
      method: HTTPMethod.PATCH,
      url: `${this.baseUrl}/shipment-group-configurations/${input.id}`,
      data: {
        ...input,
        locationKey: this.locationProvider().key,
      },
    });
  }

  async deleteShipmentGroupConfiguration(input: DeleteShipmentGroupConfigurationInput) {
    return await this.request<void>({
      method: HTTPMethod.DELETE,
      url: `${this.baseUrl}/shipment-group-configurations/${input.id}`,
    });
  }

  async manifestTruck(truck: Truck) {
    return await this.request<void>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/trucks/${truck.id}/manifests`,
      data: {
        customsBrokerId: truck.customsBrokerId,
      },
    });
  }

  async loadTruck(
    truck: Truck,
    internalShipmentGroupId: string,
    name: string,
    limit: number,
  ) {
    return await this.request<Truck>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/trucks/${truck.id}:addShipmentGroup`,
      data: {
        internalShipmentGroupId,
        palletBoxName: name,
        shipmentLimit: limit,
      },
    });
  }

  async getLinehaulCompanies() {
    return await this.request<LinehaulCompany[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/linehaulCompanies`,
    });
  }

  async getCustomsBrokers() {
    return await this.request<CustomsBroker[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/customsBrokers`,
    });
  }

  async getLoadingRestrictions() {
    return await this.request<LoadingRestriction[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/truckLoadingRestrictions`,
    });
  }

  async getInternalShipmentGroups() {
    return await this.request<InternalShipmentGroup[]>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/internal-shipment-groups`,
    });
  }

  async saveDocument(document: Document) {
    return await this.request<string | Blob>({
      method: HTTPMethod.GET,
      url: `/api/v1/documents/${document.id}/content`,
      responseType: 'blob',
      query: {
        fileName: document.fileName,
        mediaType: document.mediaType,
      },
    });
  }

  async uploadDocument(document: Document) {
    await this.request<void>({
      method: HTTPMethod.POST,
      url: `/api/v1/documents/${document.id}/upload`,
    });
  }

  async getManifestEventLog(manifestId: string) {
    return await this.request<ManifestEventLog>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/manifests/${manifestId}/event-log`,
    });
  }

  async manifestSingleActor(manifestId: string, actorName: ManifestActorName) {
    return await this.request<void>({
      method: HTTPMethod.POST,
      url: `${this.baseUrl}/manifests/${manifestId}/actors/${actorName}/runs`,
    });
  }

  async log(event: LogEvent) {
    const data = {
      ...event,
      timestamp: new Date().toISOString(),
    };

    // use a specific serializer because error properties are not enumerable
    if (data.error) {
      data.error = serializeError(data.error);
    }

    await this.request<void>({
      data,
      method: HTTPMethod.POST,
      url: '/api/v1/ui-log',
    });
  }

  async getFeatureFlags() {
    return await this.request<Dictionary<boolean>>({
      method: HTTPMethod.GET,
      url: `${this.baseUrl}/feature-flags`,
    });
  }

  async getShipments(
    {
      page,
      limit,
      status,
      isSorted,
      isManifested,
      isVoided,
      carrierAccountKeys,
      createdAfter,
      createdBefore,
      sortedAfter,
      sortedBefore,
      manifestedAfter,
      manifestedBefore,
      palletBoxId,
      references,
      sort,
      tags,
    }: GetShipmentsQuery,
  ) {
    const s = superagent
      .get(`${this.devUrl}/shipments`);

    if (limit) {
      s.query({ limit: limit.toString(10) });
    }
    if (page) {
      s.query({ page: page.toString(10) });
    }
    if (createdAfter) {
      s.query({ createdAfter });
    }
    if (createdBefore) {
      s.query({ createdBefore });
    }
    if (sortedAfter) {
      s.query({ sortedAfter });
    }
    if (sortedBefore) {
      s.query({ sortedBefore });
    }
    if (manifestedAfter) {
      s.query({ manifestedAfter });
    }
    if (manifestedBefore) {
      s.query({ manifestedBefore });
    }
    if (references) {
      s.query({ reference: references });
    }
    if (status) {
      s.query({ status });
    }
    if (isSorted !== undefined) {
      s.query({ isSorted });
    }
    if (isManifested !== undefined) {
      s.query({ isManifested });
    }
    if (isVoided !== undefined) {
      s.query({ isVoided });
    }
    if (carrierAccountKeys) {
      s.query({ carrierAccountKey: carrierAccountKeys });
    }
    if (palletBoxId) {
      s.query({ palletBoxId });
    }
    if (tags) {
      s.query({ tags });
    }
    if (sort) {
      s.query({ sort: sort.map(([field, direction]) => `${direction === 'ASC' ? '' : '-'}${field}`).join(',') });
    }

    return s.set('Authorization', this.authService.getAuthHeader())
      .then(res => res.body as GetShipmentsResult);
  }

  async createWorkflow({ name, input }: CreateWorkflowCommand): Promise<Workflow> {
    const { body } = await superagent
      .post(`${this.baseUrl}/workflows`)
      .send({
        input,
        workflow: name,
      })
      .set('Authorization', this.authService.getAuthHeader());

    return body as Workflow;
  }

  async getWorkflows(
    query: GetWorkflowsQuery,
  ): Promise<WorkflowsQueryResults> {
    const { body } = await superagent
      .get(`${this.baseUrl}/workflows`)
      .set('Authorization', this.authService.getAuthHeader())
      .query(query);

    return body as WorkflowsQueryResults;
  }

  async getWorkflow(id: string): Promise<Workflow> {
    const { body } = await superagent
      .get(`${this.baseUrl}/workflows/${id}`)
      .set('Authorization', this.authService.getAuthHeader());

    return body as Workflow;
  }

  async getWorkflowLog(
    workflowId: string,
    query: WorkflowLogQuery = {},
  ): Promise<WorkflowLogQueryResults> {
    const { body } = await superagent
      .get(`${this.baseUrl}/workflows/${workflowId}/log`)
      .set('Authorization', this.authService.getAuthHeader())
      .query(query);

    return body as WorkflowLogQueryResults;
  }

  private getDefaultOptions() {
    const authorization = this.authService.getAuthHeader();
    return {
      headers: {
        authorization,
      },
    };
  }

  private async request<T>(options: BackendApiOptions, retriable: boolean = true): Promise<T> {
    const axiosOptions: AxiosRequestConfig = {
      ...this.getDefaultOptions(),
      url:  options.url,
      method: options.method,
      params: options.query,
      data: options.data,
      responseType: options.responseType,
    };

    try {
      const result = await axios.request<T>(axiosOptions);
      return result.data;
    } catch ({ message, result, response }) {
      if (response.status === 401 && retriable) {
        const loggedIn = await this.authService.login(true);
        if (loggedIn) {
          return this.request<T>(options, false);
        }
      }

      if (response) {
        throw {
          message,
          correlationId: response.data.correlationId,
          details: response.data.details,
        };
      }

      if (result) {
        throw {
          message,
          correlationId: result.data.correlationId,
          details: result.data.details,
        };
      }

      throw {
        message,
      };
    }
  }
}
